/**
 * Function Primitive Operations
 *
 * Higher-order operations for function composition and application.
 */

import { defineOperation, PrimitiveOperation } from './operations.model';

// Type aliases for clarity
type Fn<T, R> = (arg: T) => R;
type AnyFn = (...args: unknown[]) => unknown;

// ------------------------------------------------
// FUNCTION COMPOSITION
// ------------------------------------------------

export const compose = defineOperation<AnyFn, AnyFn>({
  id: 'op.compose',
  name: 'Compose',
  symbol: '∘',
  category: 'function',
  arity: 2,
  execute: (f: AnyFn, g: AnyFn) => {
    return (...args: unknown[]) => f(g(...args));
  },
  latex: '{{f}} \\circ {{g}}',
  description: 'Composes two functions: applies g first, then f',
  example: '$(f \\circ g)(x) = f(g(x))$',
});

export const pipe = defineOperation<AnyFn, AnyFn>({
  id: 'op.pipe',
  name: 'Pipe',
  symbol: '|>',
  category: 'function',
  arity: 2,
  execute: (f: AnyFn, g: AnyFn) => {
    return (...args: unknown[]) => g(f(...args));
  },
  latex: '{{f}} \\triangleright {{g}}',
  description: 'Pipes functions: applies f first, then g',
  example: '$(f \\triangleright g)(x) = g(f(x))$',
});

// ------------------------------------------------
// FUNCTION APPLICATION
// ------------------------------------------------

export const apply = defineOperation<unknown, unknown>({
  id: 'op.apply',
  name: 'Apply',
  symbol: 'f(x)',
  category: 'function',
  arity: 2,
  execute: (f: AnyFn, x: unknown) => f(x),
  latex: '{{f}}({{x}})',
  description: 'Applies a function to an argument',
  example: '$f(3) = 9$ where $f(x) = x^2$',
});

export const partial = defineOperation<unknown, AnyFn>({
  id: 'op.partial',
  name: 'Partial Application',
  symbol: 'f(a, _)',
  category: 'function',
  arity: 2,
  execute: (f: AnyFn, ...boundArgs: unknown[]) => {
    return (...remainingArgs: unknown[]) => f(...boundArgs, ...remainingArgs);
  },
  latex: '{{f}}({{a}}, \\_)',
  description: 'Partially applies arguments to a function',
  example: '$\\text{add}(3, \\_) \\Rightarrow$ function that adds 3',
});

// ------------------------------------------------
// HIGHER-ORDER OPERATIONS
// ------------------------------------------------

export const map = defineOperation<unknown, unknown[]>({
  id: 'op.map',
  name: 'Map',
  symbol: 'map',
  category: 'function',
  arity: 2,
  execute: (f: Fn<unknown, unknown>, arr: unknown[]) => arr.map(f),
  latex: '\\text{map}({{f}}, {{arr}})',
  description: 'Applies a function to each element of an array',
  example: '$\\text{map}(x^2, [1,2,3]) = [1,4,9]$',
});

export const filter = defineOperation<unknown, unknown[]>({
  id: 'op.filter',
  name: 'Filter',
  symbol: 'filter',
  category: 'function',
  arity: 2,
  execute: (predicate: Fn<unknown, boolean>, arr: unknown[]) => (arr as unknown[]).filter(predicate),
  latex: '\\text{filter}({{p}}, {{arr}})',
  description: 'Keeps only elements that satisfy a predicate',
  example: '$\\text{filter}(x > 2, [1,2,3,4]) = [3,4]$',
});

export const reduce = defineOperation<unknown, unknown>({
  id: 'op.reduce',
  name: 'Reduce',
  symbol: 'reduce',
  category: 'function',
  arity: 3,
  execute: (f: (acc: unknown, val: unknown) => unknown, initial: unknown, arr: unknown[]) => arr.reduce(f, initial),
  latex: '\\text{reduce}({{f}}, {{init}}, {{arr}})',
  description: 'Reduces an array to a single value using an accumulator',
  example: '$\\text{reduce}(+, 0, [1,2,3]) = 6$',
});

// ------------------------------------------------
// EXPORT ALL FUNCTION OPERATIONS
// ------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const functionOperations: Array<PrimitiveOperation<any, any>> = [
  compose,
  pipe,
  apply,
  partial,
  map,
  filter,
  reduce,
];
